// @ts-nocheck
import React, { useMemo } from 'react';

import FormStatic from '../../common/FormStatic';

import TimeLabel from '../../common/TimeLabel';
import FormattedDate from '../../common/FormattedDate';
import { isPowerUser } from '../../auth/authUtils';

import CompanyLink from '../../common/links/CompanyLink';
import UserLink from '../../common/links/UserLink';
import { toast } from 'react-toastify';
import CollapsePanel from '../../common/CollapsePanel';
import { useTranslation } from 'react-i18next';
import { downloadPurchaseOrder } from '../../../api/requestApi';
import { useAppSelector } from '../../../hooks';

export interface RequestDetailsProps {
  request: Record<string, unknown>;
  numberOfKeysRequiringAccountDetails?: number;
  numberOfAccountDetailsUploadedForKeysRequiringAccountDetails?: number;
}

function RequestDetails({
  request,
  numberOfKeysRequiringAccountDetails,
  numberOfAccountDetailsUploadedForKeysRequiringAccountDetails,
}: RequestDetailsProps) {
  const { t } = useTranslation();

  const currentUser = useAppSelector((state) => state.auth);

  const handleDownloadPurchaseOrder = (file) => {
    downloadPurchaseOrder(request.requestGroupId, file.fileId).catch(() => {
      toast.error(t('Failed to download purchase order'));
    });
  };

  // cache the response from this function
  const getColumnCount = useMemo(() => {
    let columnCount = 4;
    let fieldCount = 7;
    if (isPowerUser(currentUser)) fieldCount++;
    if (request.fileOnKeyRequestFilename) fieldCount++;
    if (request.showRequiredByDate) fieldCount++;

    if (fieldCount % 3 === 0) {
      columnCount = 3;
    }
    return `col-count-${columnCount}`;
  }, [
    currentUser,
    request.fileOnKeyRequestFilename,
    request.showRequiredByDate,
  ]);

  const keysRequiringAccountDetails = numberOfKeysRequiringAccountDetails ?? 0;
  const uploadedAccountDetailsForRequiredKeys =
    numberOfAccountDetailsUploadedForKeysRequiringAccountDetails ?? 0;

  return (
    <>
      {request.endUserDetailsRequired &&
        keysRequiringAccountDetails > 0 &&
        uploadedAccountDetailsForRequiredKeys < keysRequiringAccountDetails && (
          <div className="alert alert-warning twinkle flex-shrink-0 ">
            <div>
              {t(
                'This workflow requires account details to be uploaded. You have currently uploaded {numberOfAccountDetails} account details for the {numberOfKeys} keys requested',
                {
                  numberOfAccountDetails: uploadedAccountDetailsForRequiredKeys,
                  numberOfKeys: keysRequiringAccountDetails,
                }
              )}
            </div>
          </div>
        )}
      <CollapsePanel
        storageKey={'keyRequestDetails'}
        header={<h2 className="no-margin">{t('Request details')}</h2>}
      >
        <div className={`cols ${getColumnCount}`}>
          <FormStatic label={t('Reference')}>
            {request.groupReference}
          </FormStatic>
          <FormStatic label={t('PO number')}>{request.poNumber}</FormStatic>
          <FormStatic label={t('Requested by')}>
            <UserLink
              id={request.requestedById}
              name={request.requestedByFullName}
            />
            <div className="ms-3">{request.requestedByCompanyName}</div>
          </FormStatic>
          <FormStatic label={t('Company')}>
            <CompanyLink id={request.companyId} name={request.companyName} />
          </FormStatic>
          <FormStatic label={t('Requested')}>
            <FormattedDate value={request.requestedDate} variant="noHtml" />
          </FormStatic>
          <FormStatic label={t('Elapsed time')}>
            <TimeLabel
              elapsedMinutesFormatted={request.elapsedMinutesFormatted}
              breached={request.isSLABreached}
              ongoing={request?.requestCompletionDate ? false : true}
            />
          </FormStatic>
          {request.showRequiredByDate && (
            <FormStatic label={t('Required by')}>
              <FormattedDate value={request.requiredByDate} variant="dateOnly" />
            </FormStatic>
          )}
          <FormStatic label={t('Justification')}>
            {request.justification || t('none')}
          </FormStatic>
          {request.files && (
            <FormStatic label={t('Purchase order')} className="flex-column">
              {request.files.map((file) => (
                <div key={file.fileId} className="w-100">
                  <span
                    className="a"
                    onClick={() => handleDownloadPurchaseOrder(file)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleDownloadPurchaseOrder(file);
                      }
                    }}
                    tabIndex={0}
                  >
                    {file.fileName}
                  </span>
                </div>
              ))}
            </FormStatic>
          )}
        </div>
      </CollapsePanel>
    </>
  );
}

export default RequestDetails;
